import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import PaymentModal from '@/components/modals/PaymentModal'
import { RECURRENT_CHARGES } from '@/constants'
import useParamSelector from '@/hooks/useParamSelector'
import { usePostMessagePurchaseEvents } from '@/hooks/usePostMessagePurchaseEvents'
import { useTypedDispatch, useTypedSelector } from '@/store/hooks'
import { clearPaymentInfo, createCharge, declineCharge } from '@/store/payment'
import { type PaymentIntent } from '@/store/payment/interfaces'
import { getPaymentIntent } from '@/store/payment/selectors'
import { canBuyProduct, getPaymentCharge } from '@/store/selectors'
import { isAgentApp } from '@/utils'
import { isInIframe } from '@/utils/browser'
import { PM_PAYMENT_EVENT } from '@/utils/postMessage/constants'
import { sendPostMessage } from '@/utils/postMessage/utils'

type Props = {
  id: string
  children: ReactNode
}

type Context = {
  onPaymentModalClose: () => void
  closeModal: () => void
  openModal: () => void
  paymentIntent: PaymentIntent | null
}

export const OneClickPaymentContext = createContext<Context>({
  onPaymentModalClose: () => null,
  closeModal: () => null,
  openModal: () => null,
  paymentIntent: null,
})

const OneClickPaymentContextProvider = ({ children, id }: Props) => {
  const dispatch = useTypedDispatch()
  const paymentIntent = useParamSelector(getPaymentIntent, id)
  const canUserBuy = useParamSelector(
    canBuyProduct,
    paymentIntent?.metadata.product
  )
  const charge = useTypedSelector(getPaymentCharge)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = useCallback(() => {
    if (canUserBuy) {
      dispatch(createCharge({ paymentIntent }))
    }

    if (isInIframe()) {
      sendPostMessage(PM_PAYMENT_EVENT.REGISTER_TRANSACTION_PENDING, {
        paymentIntent,
      })
    }

    setIsModalOpen(true)
  }, [paymentIntent, canUserBuy, dispatch])

  const onPaymentModalClose = useCallback(() => {
    dispatch(clearPaymentInfo())
    setIsModalOpen(false)
  }, [dispatch])

  const closeModal = useCallback(() => {
    if (!isInIframe() && charge?.status === RECURRENT_CHARGES.STATUS.PENDING) {
      dispatch(declineCharge({ preventRedirect: true }))
    }

    onPaymentModalClose()
  }, [charge?.status, onPaymentModalClose, dispatch])

  usePostMessagePurchaseEvents({
    isOpen: isModalOpen,
    onModalClose: closeModal,
  })

  const value = useMemo(
    () => ({
      onPaymentModalClose,
      closeModal,
      openModal,
      paymentIntent,
    }),
    [closeModal, onPaymentModalClose, openModal, paymentIntent]
  )

  return (
    <OneClickPaymentContext.Provider value={value}>
      {children}

      {isModalOpen && !isAgentApp && <PaymentModal id={id} />}
    </OneClickPaymentContext.Provider>
  )
}

const useOneClickPaymentContext = () => {
  const context = useContext(OneClickPaymentContext)

  if (context === undefined) {
    throw new Error(
      'useOneClickPaymentContext must be used within an OneClickPaymentProvider'
    )
  }

  return context
}

export { OneClickPaymentContextProvider, useOneClickPaymentContext }
